import React, { cloneElement, ReactElement, ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { Button } from 'components/Form/Button/Button';

import { BasePopup } from '../BasePopup/BasePopup';

type PopupProps = {
  children?: React.ReactNode;
  CancelButton?: React.FC<{ cancelButtonRef: React.RefObject<HTMLButtonElement | null> }>;
  PrimaryButton?: React.FC;
  testId?: string;
  open: boolean;
  title?: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  fitContent?: boolean;
  icon?: ReactElement<{ className?: string; 'aria-hidden'?: string }>;
  iconWrapperClassName?: string;
};

export function Popup({
  children,
  CancelButton,
  PrimaryButton,
  open,
  title,
  subtitle,
  onClose,
  testId,
  fitContent,
  icon,
  iconWrapperClassName,
}: PopupProps) {
  const { t } = useTranslation();
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <BasePopup
      open={open}
      setOpen={onClose}
      initialFocus={cancelButtonRef}
      testId={testId}
      width={fitContent ? 'fit-content' : undefined}
      className="flex max-h-[calc(100dvh-2rem)] flex-col sm:max-h-[calc(100dvh-4rem)]"
    >
      <div className="flex shrink-0 gap-4 rounded-t-lg bg-white px-4 py-5 sm:px-6">
        {icon ? (
          <div
            className={cn(
              'flex size-10 shrink-0 grow-0 items-center justify-center rounded-full',
              iconWrapperClassName,
            )}
          >
            {cloneElement(icon, {
              ...icon.props,
              className: cn(icon.props.className, 'size-6'),
              'aria-hidden': 'true',
            })}
          </div>
        ) : null}

        {!!title && (
          <div className={'flex flex-col gap-2'}>
            {title ? <h3 className="text-lg font-semibold text-gray-900">{title}</h3> : null}
            {subtitle ? <div className="mt-1 text-sm text-gray-500">{subtitle}</div> : null}
          </div>
        )}
      </div>
      {!PrimaryButton && (
        <div className="absolute top-5 right-4">
          <Button
            size="extra-small"
            variant="minimal"
            LeftIcon={XMarkIcon}
            iconOnly
            onClick={onClose}
            aria-label={t('common:close')}
            analyticsId="popup_close"
          />
        </div>
      )}
      {children ? (
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto bg-white px-6 pb-4',
            title ? '' : 'rounded-t-lg',
            PrimaryButton || CancelButton ? '' : 'rounded-b-lg',
          )}
        >
          {children}
        </div>
      ) : null}
      {PrimaryButton || CancelButton ? (
        <div className="shrink-0 gap-x-3 rounded-b-lg bg-white px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
          {PrimaryButton && <PrimaryButton />}
          {CancelButton && <CancelButton cancelButtonRef={cancelButtonRef} />}
        </div>
      ) : null}
    </BasePopup>
  );
}
